import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import SurvivabilityGauge from "../../components/alpharank/SurvivabilityGauge";
import ScalabilityDisplay from "../../components/alpharank/ScalabilityDisplay";
import RegimeDetection from "../../components/alpharank/RegimeDetection";
import RiskFlagsDetail from "../../components/alpharank/RiskFlagsDetail";

const AnalyticsPage = () => {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("account_id");

  const [account, setAccount] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!accountId) {
        setLoading(false);
        return;
      }

      try {
        const [accountsData, detailed] = await Promise.all([
          api.getAccounts(),
          api.getDetailedAlphaRank(accountId).catch(() => null)
        ]);

        const currentAccount = accountsData.find(a =>
          (a.ID || a.id) === accountId
        );

        setAccount(currentAccount);
        setDetailedData(detailed);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId]);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        Loading advanced analytics...
      </div>
    );
  }

  if (!accountId || !account) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        <p>No account selected.</p>
        <Link to="/trader" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const accountNumber = account.AccountNumber || account.account_number || 'N/A';
  const pillars = detailedData?.pillars || {};
  const alpharank = detailedData?.alpharank || {};
  const survivability = detailedData?.survivability;
  const scalability = detailedData?.scalability;
  const regime = detailedData?.regime;
  const riskFlags = detailedData?.risk_flags;
  const metrics = detailedData?.metrics || {};

  // Section component
  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{
        fontSize: 18,
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: 16,
        paddingBottom: 8,
        borderBottom: '2px solid #e5e7eb'
      }}>
        {title}
      </h3>
      {children}
    </div>
  );

  return (
    <div style={{ padding: '32px 16px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Link
          to={`/trader?account_id=${accountId}`}
          style={{
            display: 'inline-block',
            marginBottom: 16,
            color: '#3b82f6',
            textDecoration: 'none',
            fontSize: 14,
            fontWeight: 600
          }}
        >
          Back to Dashboard
        </Link>

        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px 0', color: '#1f2937' }}>
          Advanced Analytics
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          Complete risk analysis • Account #{accountNumber}
        </p>
      </div>

      {/* AlphaRank Overview */}
      <Section title=" AlphaRank Overview">
        <div style={{
          background: 'rgba(255,255,255,0.96)',
          borderRadius: 12,
          padding: 20,
          border: '1px solid rgba(0,0,0,0.08)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 16
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>AlphaScore</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#3b82f6' }}>
              {alpharank.score?.toFixed(1) || '--'}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Grade</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#f59e0b' }}>
              {alpharank.grade || 'N/A'}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Tier</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#6b7280', marginTop: 8 }}>
              {alpharank.tier || 'N/A'}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Trades</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#10b981' }}>
              {metrics.total_trades || 0}
            </div>
          </div>
        </div>
      </Section>

      {/* Survivability & Scalability */}
      <Section title="Account Health Indicators">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          <SurvivabilityGauge survivability={survivability} />
          <ScalabilityDisplay scalability={scalability} />
        </div>
      </Section>

      {/* Regime Detection */}
      <Section title=" Trading Regime Analysis">
        <RegimeDetection regime={regime} />
      </Section>

      {/* Risk Flags */}
      <Section title="Risk Analysis">
        <RiskFlagsDetail riskFlags={riskFlags} />
      </Section>

      {/* AlphaRank Pillars (P1-P7) */}
      <Section title=" AlphaRank Pillars (P1-P7)">
        <div style={{
          background: 'rgba(255,255,255,0.96)',
          borderRadius: 12,
          padding: 20,
          border: '1px solid rgba(0,0,0,0.08)'
        }}>
          {Object.entries(pillars).map(([key, value]) => {
            const pillarNames = {
              'P1_Profitability': 'P1: Profitability',
              'P2_Risk': 'P2: Consistency',
              'P3_Consistency': 'P3: Risk Management',
              'P4_Recovery': 'P4: Recovery',
              'P5_Edge': 'P5: Trading Edge',
              'P6_Discipline': 'P6: Discipline',
              'P7_TrackRecord': 'P7: Track Record'
            };

            const pillarName = pillarNames[key] || key;
            const score = typeof value === 'number' ? value : 0;
            
            const getColor = (s) => {
              if (s >= 70) return '#10b981';
              if (s >= 50) return '#3b82f6';
              if (s >= 30) return '#f59e0b';
              return '#ef4444';
            };

            const color = getColor(score);

            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                    {pillarName}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color }}>
                    {score.toFixed(1)}
                  </span>
                </div>
                <div style={{
                  height: 10,
                  background: '#f3f4f6',
                  borderRadius: 5,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${score}%`,
                    background: color,
                    borderRadius: 5,
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Trading Metrics Summary */}
      <Section title="Trading Statistics">
        <div style={{
          background: 'rgba(255,255,255,0.96)',
          borderRadius: 12,
          padding: 20,
          border: '1px solid rgba(0,0,0,0.08)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Win Rate</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>
              {metrics.win_rate?.toFixed(1) || 0}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Net Profit</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: metrics.net_profit >= 0 ? '#10b981' : '#ef4444' }}>
              ${metrics.net_profit?.toFixed(2) || 0}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Max Drawdown</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>
              {metrics.max_drawdown_pct?.toFixed(1) || 0}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Current Balance</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1f2937' }}>
              ${metrics.current_balance?.toFixed(2) || 0}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Current Equity</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: metrics.current_equity < metrics.current_balance ? '#ef4444' : '#10b981' }}>
              ${metrics.current_equity?.toFixed(2) || 0}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Floating P&L</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: (metrics.current_equity - metrics.current_balance) >= 0 ? '#10b981' : '#ef4444' }}>
              ${((metrics.current_equity || 0) - (metrics.current_balance || 0)).toFixed(2)}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default AnalyticsPage;
